import { useEffect, useRef } from 'react'

/**
 * The trailing window a burst of realtime invalidations is folded into
 * (TASK-41CE1DE2 fix cycle 1). The server publishes TWO `task.*` frames for one
 * status change (`task.updated`, then `task.status-changed`), each mapped to
 * `invalidateBoard`; reloading on each one cost two full reads per change. A
 * reload now starts once the frames have been quiet for this long — one reload
 * per burst, still well under a second.
 */
export const REALTIME_RELOAD_COALESCE_MS = 200

/**
 * Runs `reload` ONCE per burst of realtime invalidations: every change of
 * `signal` (a nonce, or several nonces joined into one key) restarts a trailing
 * timer, and `reload` runs when the signal has been quiet for
 * {@link REALTIME_RELOAD_COALESCE_MS}.
 *
 * - The value the component MOUNTED with never schedules anything — the caller's
 *   own mount load owns that (a remount after a close sees the reset nonce).
 * - The cleanup only clears the timer; it must never reset a slice (memory
 *   px-effect-cleanup-on-nonce-dep-resets-the-slice).
 * - `reload` must be stable (`useCallback` keyed on identity), or every render
 *   would restart the timer.
 * - A frame that lands while a load is in flight is still followed up by
 *   `store/cacheFollowUps.ts`; a timer that fires on a fresh cache is vetoed by
 *   the thunk's own `shouldFetch` condition.
 */
export function useCoalescedRealtimeReload(signal: number | string, reload: () => void): void {
  const mountedWith = useRef(signal)

  useEffect(() => {
    if (signal === mountedWith.current) return
    const timer = window.setTimeout(reload, REALTIME_RELOAD_COALESCE_MS)
    return () => {
      window.clearTimeout(timer)
    }
  }, [signal, reload])
}
